import React, { useCallback, useEffect, useState } from 'react';
import Spacing from '../Spacing';
import { FolderOutput, FolderSync, Loader, Plus, Search } from 'lucide-react';
import Filter from '../Filter';
import DataTablePagination from '../DataTablePagination';
import RejectionTrackerDataTable from '../DataTable/RejectionTrackerDataTable';
import { can_visit, debounce, getPractice, notify, numberWithCommas } from '../../helpers';
import { useDocumentTitle } from '../../hooks/customDocumentTitleHook';
import TabMenu from '../TabMenu';
import { changeStatusRejections, deleteBulkRejectionData, deleteRejectionData, exportAllRejections, exportRejections, fetchRejections, getRejectionOverview, notifyPracticeData } from '../../services/rejectionTracker';
import RejectionAddModal from '../Modal/RejectionAddModal';
import { getLocation } from '../../services/appointment';
import RejectionImportModal from '../Modal/RejectionImportModal';
import BulkDeleteModal from '../Modal/BulkDeleteModal';
import RejectionOverViewSection from '../Section/RejectionOverViewSection';

export default function RejectionTracker({selectedPracticeChange, fetchChangePracticeData, smsLimit}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }

  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);
  const [dataLoading, setDataLoading] = useState(true);
  const [rejections, setRejections] = useState([]);
  const [allLocation, setAllLocation] = useState([]);
  const [selectedReferral, setSelectedReferral] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [exportAllButtonLoader, setExportAllButtonLoader] = useState(false);
  const [exportButtonLoader, setExportButtonLoader] = useState(false);
  const [rejectionAdd, setRejectionAdd] = useState(false);
  const [rejectionEdit, setRejectionEdit] = useState(false);
  const [rejectionCopy, setRejectionCopy] = useState(false);
  const [rejectionImport, setRejectionImport] = useState(false);
  const [rejectionBulkDelete, setRejectionBulkDelete] = useState(false);
  const [rejectionOverviewLoader, setRejectionOverviewLoader] = useState(true);
  const [rejectionOverviewDataCounts, setRejectionOverviewDataCounts] =
    useState({
      total_rejection_count: 0,
      total_pending_count: 0,
      total_paid_count: 0,
      total_before_resubmit_count: 0,
      total_after_resubmit_count: 0,
      total_resubmitted_count: 0,
      total_again_denied_count: 0,
      total_need_clinics_help: 0,
      total_write_off_count: 0,
    });

  const [filter, setFilter] = useState({
    page: 1,
    per_page: 10,
    status: "",
    search_term: "",
    first_name: "",
    last_name: "",
    dob: "",
    insurance_name: "",
    insurance_ids: "",
    dos_from: "",
    dos_to: "",
    resubmit_date_from: "",
    resubmit_date_to: "",
    account_id: account_id,
    location_id: location_id,
    order_by: [],
  });

  const [paginationCount, setPaginationCount] = useState({
    totalCount: 0,
    pendingCount: 0,
    paidCount: 0,
    resubmitCount: 0,
    againDeniedCount: 0,
    needClinicsHelpCount: 0,
    writeOffCount: 0,
    totalPages: 0,
    account_id: account_id,
    location_id: location_id,
  });
  const [checkedIds, setCheckedIds] = useState([]);
  const [checkSelected, setCheckSelected] = useState(checkedIds.length > 0);
  const [globalCheckedIds, setGlobalCheckedIds] = useState(new Set());

  const [patientFormData, setPatientFormData] = useState(filter);


  useEffect(() => {
    setCheckSelected(checkedIds.length > 0 ? true : false);
  }, [checkedIds]);

  useEffect(() => {
    loadLocations(account_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getRejectionOverviewDataCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRejectionOverviewDataCounts = async () => {
    setRejectionOverviewLoader(true);
    try {
      const response = await getRejectionOverview({
        location_id: location_id,
        account_id: account_id,
      });
      
      if (response) {
        setRejectionOverviewDataCounts(response?.data);
        setRejectionOverviewLoader(false);
      }
    } catch (error) {
      // console.log("🚀 ~ getRejectionOverviewDataCounts ~ error:", error);
    }
  };

  const loadLocations = async (id, accountId) => {
    try {
      const locationResponse = await getLocation(id, accountId);
      setAllLocation(locationResponse.data || []);
    } catch (error) {
      // console.log("🚀 ~ loadLocations ~ error:", error);
    }
  };

  useEffect(() => {
    loadRejections(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadRejections = async (filter) => {
    setFilter(filter);
    let isCanceled = false;

    try {
      const rejectionResponse = await fetchRejections(filter);
      setRejections(rejectionResponse.data.results);
      setPaginationCount({
        totalCount: rejectionResponse?.data?.count ?? 0,
        pendingCount: rejectionResponse?.data?.pending_count ?? 0,
        paidCount: rejectionResponse?.data?.paid_count ?? 0,
        resubmitCount: rejectionResponse?.data?.resubmitted_count ?? 0,
        againDeniedCount: rejectionResponse?.data?.again_denied_count ?? 0,
        needClinicsHelpCount: rejectionResponse?.data?.need_clinics_help ?? 0,
        writeOffCount: rejectionResponse?.data?.write_off_count ?? 0,
        totalPages: rejectionResponse?.data?.total_pages,
      });
      if (checkedIds.length < 1) {
        setSelectAll(false);
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        // console.log("🚀 ~ loadRejections ~ error:", error);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  const getRejectionsByStatus = (selectedTabValue) => {
    const tabValue = selectedTabValue.replace(/\s*\(.*?\)\s*/g, "");
    const statusValue = tabValue === "All" ? "" : tabValue;
    setFilter({ ...filter, status: statusValue, page: 1 });
  };

  const tabMenuData = [
    `All (${paginationCount.totalCount})`,
    `Need clinic’s help (${paginationCount.needClinicsHelpCount})`,
    `Pending (${paginationCount.pendingCount})`,
    `Write off (${paginationCount.writeOffCount})`,
    `Again denied (${paginationCount.againDeniedCount})`,
    `Resubmitted (${paginationCount.resubmitCount})`,
    `Paid (${paginationCount.paidCount})`,
  ];

  const handleCheckboxChange = (id, rowData) => {
    const isChecked = checkedIds.includes(id);
    if (isChecked) {
      setCheckedIds(checkedIds.filter((checkedId) => checkedId !== id));
    } else {
      setCheckedIds([...checkedIds, id]);
    }
    setRejections((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, checked: !row.checked } : row
      )
    );
    setGlobalCheckedIds((prevCheckedIds) => {
      const updatedCheckedIds = new Set(prevCheckedIds);
      if (updatedCheckedIds.has(id)) {
        updatedCheckedIds.delete(id);
      } else {
        updatedCheckedIds.add(id);
      }
      return updatedCheckedIds;
    });
  };

  const handleSelectAll = (currentPageDataInput) => {
    const currentPageData = Array.isArray(currentPageDataInput)
      ? currentPageDataInput
      : [];
    
    if (selectAll) {
      const newCheckedIds = checkedIds.filter(
        (id) => !currentPageData?.some((row) => row.id === id)
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData?.forEach((row) => updatedCheckedIds.delete(row.id));
        return updatedCheckedIds;
      });
    } else {
      const newCheckedIds = Array.from(
        new Set([...checkedIds, ...currentPageData?.map((row) => row.id)])
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData?.forEach((row) => updatedCheckedIds.add(row.id));
        return updatedCheckedIds;
      });
    }

    setSelectAll(!selectAll);

    setRejections((prevData) =>
      prevData.map((row) => ({
        ...row,
        checked: !selectAll,
      }))
    );
  };

  const handlePageChange = (newPage) => {
    setDataLoading(true);
    setFilter({ ...filter, page: newPage });
  };

  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setDataLoading(true);
    debouncedSetFilter(newFilter);
  };

  const handleChangeRowsPerPage = (value) => {
    setDataLoading(true);
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
  };

  const changeStatus = async (id, status) => {
    let isCanceled = false;
    setDataLoading(true);

    try {
      const statusData = {
        account_id: account_id,
        location_id: location_id,
        id: id,
        status: status,
      };

      const changeStatus = await changeStatusRejections(statusData);
      if(changeStatus) {
        if (changeStatus?.detail) {
          notify('error', changeStatus?.detail);
        } else { 
          if(changeStatus?.data?.success){
            notify("success", changeStatus?.data?.success);
            await loadRejections(filter);
            getRejectionOverviewDataCounts();
          } else {
            notify("error", changeStatus?.data?.error);
          }
        }
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        // console.log("🚀 ~ changeStatus ~ error:", error);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  const handleDeleteRecord = async (id) => {
    setDataLoading(true);

    try {
      const deleteRejection = await deleteRejectionData(id, account_id, location_id);
      if(deleteRejection) {
        if (deleteRejection?.detail) {
          notify('error', deleteRejection?.detail);
        } else { 
          if(deleteRejection?.data?.success){
            notify("success", deleteRejection?.data?.success, 0.5);
            await loadRejections(filter);
            getRejectionOverviewDataCounts();
          } else {
            notify("error", deleteRejection?.data?.error);
          }
        }
      }
    } catch (error) {
      // console.log("🚀 ~ handleDeleteRecord ~ error:", error);
    } finally {
      setDataLoading(false);
    }
  };

  const handleBulkDeleteRecord = async (e) => {
    setDataLoading(true);

    try {
      const deleteData = {
        account_id: account_id,
        location_id: location_id,
        selected_id: checkedIds,
      };

      const response = await deleteBulkRejectionData(deleteData);
      if (response) {
        if (response?.detail) {
          notify("error", response?.detail);
        } else {
          if(response?.data?.success){
            notify("success", response?.data?.success, 0.5);
            await loadRejections(filter);
            getRejectionOverviewDataCounts();
          } else {
            notify("error", response?.data?.error);
          }
        }
      }
    } catch (error) {
      // console.log("🚀 ~ handleBulkDeleteRecord ~ error:", error);
    } finally {
      setDataLoading(false);
    }
  };

  const handleNotifyPractice = async (id) => {
    let isCanceled = false;
    setDataLoading(true);

    try {
      const notifyData = {
        account_id: account_id,
        location_id: location_id,
        id: id,
      };

      const response = await notifyPracticeData(notifyData);
      if (response) {
        if (response?.detail) {
          notify("error", response?.detail);
        } else {
          if(response?.data?.success){
            notify("success", response?.data?.success, 0.5);
            await loadRejections(filter);
          } else {
            notify("error", response?.data?.error);
          }
        }
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        notify("error", "Something went wrong");
        // console.log("🚀 ~ handleBulkDeleteRecord ~ error:", error);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
    });
    loadLocations(account_id);
    getRejectionOverviewDataCounts();
    setCheckedIds([]);
    setGlobalCheckedIds(new Set());
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  const handleExportAll = async (e) => {
    setExportAllButtonLoader(true);
    try {
      const response = await exportAllRejections(filter);

      if (response && response.data) {
        const responseData = response.data;
        const dataBlob = new Blob([responseData], {
          type: "text/csv;charset=utf-8;",
        });
        const generateURL = window.URL.createObjectURL(dataBlob);

        // File Name Generate
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const day = String(now.getDate()).padStart(2, "0");
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        const seconds = String(now.getSeconds()).padStart(2, "0");
        const filename = `Rejection_list_${year}_${month}_${day}_${hours}_${minutes}_${seconds}.csv`;

        const link = document.createElement("a");
        link.href = generateURL;
        link.setAttribute("download", filename);

        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        window.URL.revokeObjectURL(generateURL);
      }
      
    } catch (error) {
      // console.log("🚀 ~ ExporPatients ~ error:", error);
    } finally {
      setExportAllButtonLoader(false);
    }
  };

  const handleExport = async (e) => {
    setExportButtonLoader(true);
    try {
      const deleteData = {
        account_id: account_id,
        location_id: location_id,
        ids: checkedIds,
      };
      const response = await exportRejections(deleteData);

      if (response && response.data) {
        const responseData = response.data;
        const dataBlob = new Blob([responseData], {
          type: "text/csv;charset=utf-8;",
        });
        const generateURL = window.URL.createObjectURL(dataBlob);

        // File Name Generate
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const day = String(now.getDate()).padStart(2, "0");
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        const seconds = String(now.getSeconds()).padStart(2, "0");
        const filename = `Rejection_list_${year}_${month}_${day}_${hours}_${minutes}_${seconds}.csv`;

        const link = document.createElement("a");
        link.href = generateURL;
        link.setAttribute("download", filename);

        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        window.URL.revokeObjectURL(generateURL);
      }
      
    } catch (error) {
      // console.log("🚀 ~ ExporPatients ~ error:", error);
    } finally {
      setExportButtonLoader(false);
    }
  };

  useDocumentTitle("Rejection Tracker");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          <i
            className="docpro_white_color"
            style={{ backgroundColor: '#793aaf' }}
          >
            <FolderSync width={18} height={18} />
          </i>
          Rejection Tracker
          <span>{numberWithCommas(rejectionOverviewDataCounts?.total_rejection_count || 0)}</span>
          {/* <span>{numberWithCommas(paginationCount.totalCount)}</span> */}
        </h2>
      </div>

      {(can_visit('rejection_tracker') && can_visit('overview_rejection_tracker')) && (
        <>
          <Spacing lg="4" sm="4" />
          <RejectionOverViewSection 
            rejectionOverviewDataCounts={rejectionOverviewDataCounts}
            rejectionOverviewLoader={rejectionOverviewLoader}
          />
          <Spacing lg="15" sm="15" />
        </>
      )}

      <TabMenu
        tabMenuData={tabMenuData}
        filter={false}
        handleSelectTab={getRejectionsByStatus}
        setDataLoading={setDataLoading}
      />
      <Spacing lg="12" sm="12" />
      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings small_heading docpro_grid_gap_10">
          <div className="flex items-center docpro_grid_gap_10">
            <form className="docpro_search_form">
              <input
                type="text"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>
            {(can_visit("rejection_tracker") && can_visit('_bulk_export')) && (
              <button 
                className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
                style={{ whiteSpace: 'nowrap', }}
                onClick={handleExportAll}
                disabled = {exportAllButtonLoader ? true : false}
              >
                <FolderOutput width={20} height={20} />
                Export All
                {exportAllButtonLoader && (
                  <span className="docpro_loading">
                    <Loader width={20} height={20} />
                  </span>
                )}
              </button>
            )}
          </div>

          <div className='docpro_data_table_btns p-0'>
            {(can_visit("rejection_tracker") && can_visit('rejection_tracker_create')) && (
              <>
                <button 
                  className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
                  onClick={() => {
                    setRejectionImport(!rejectionImport);
                    setPreviousScrollPosition(window.scrollY);
                    window.scrollTo(0, 0);
                  }}
                >
                  <Plus width={16} height={16} />
                  Import
                </button>
                <button 
                  className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
                  onClick={() => {
                    setRejectionAdd(!rejectionAdd);
                    setPreviousScrollPosition(window.scrollY);
                    window.scrollTo(0, 0);
                  }}
                >
                  <Plus width={16} height={16} />
                  Add Rejection
                </button>
              </>
            )}

            <Filter
              filterType="rejection"
              reloadData={loadRejections}
              reloadDataFilter={filter}
              setDataLoading={setDataLoading}
              patientFormData={patientFormData}
              setPatientFormData={setPatientFormData}
            />
          </div>
        </div>

        <RejectionTrackerDataTable 
          tableData={rejections}
          handleSelectAll={handleSelectAll}
          handleCheckboxChange={handleCheckboxChange}
          reloadData={loadRejections}
          reloadOverviewData={getRejectionOverviewDataCounts}
          reloadDataFilter={filter}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          handleDeleteRecord={handleDeleteRecord}
          checkSelected={checkSelected}
          exportData={handleExport}
          exportButtonLoader={exportButtonLoader}
          rejectionEdit={rejectionEdit}
          setRejectionEdit={setRejectionEdit}
          rejectionCopy={rejectionCopy}
          setRejectionCopy={setRejectionCopy}
          setSelectedReferral={setSelectedReferral}
          changeStatus={changeStatus}
          rejectionBulkDelete={rejectionBulkDelete}
          setRejectionBulkDelete={setRejectionBulkDelete}
          handleNotifyPractice={handleNotifyPractice}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
          checkedIds={checkedIds}
          setCheckedIds={setCheckedIds}
          globalCheckedIds={globalCheckedIds}
          setGlobalCheckedIds={setGlobalCheckedIds}
        />
        {(rejections.length > 0 && !dataLoading) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={paginationCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={paginationCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>

      {rejectionImport && (
        <RejectionImportModal
          account_id={account_id}
          location_id={location_id}
          reloadData={loadRejections}
          reloadOverviewData={getRejectionOverviewDataCounts}
          reloadDataFilter={filter}
          setFilter={setFilter}
          rejectionImport={rejectionImport}
          setRejectionImport={setRejectionImport}
          previousScrollPosition={previousScrollPosition}
        />
      )}

      {rejectionAdd && (
        <RejectionAddModal
          reloadData={loadRejections}
          reloadOverviewData={getRejectionOverviewDataCounts}
          reloadDataFilter={filter}
          account_id={account_id}
          location_id={location_id}
          setFilter={setFilter}
          rejectionOpen={rejectionAdd}
          setRejectionOpen={setRejectionAdd}
          allLocation={allLocation}
          modalType='Add'
          previousScrollPosition={previousScrollPosition}
        />
      )}

      {rejectionEdit && (
        <RejectionAddModal
          reloadData={loadRejections}
          reloadOverviewData={getRejectionOverviewDataCounts}
          reloadDataFilter={filter}
          account_id={account_id}
          location_id={location_id}
          setFilter={setFilter}
          rejectionOpen={rejectionEdit}
          setRejectionOpen={setRejectionEdit}
          allLocation={allLocation}
          modalType='Edit'
          editValue={selectedReferral}
          previousScrollPosition={previousScrollPosition}
        />
      )}

      {rejectionCopy && (
        <RejectionAddModal
          reloadData={loadRejections}
          reloadOverviewData={getRejectionOverviewDataCounts}
          reloadDataFilter={filter}
          account_id={account_id}
          location_id={location_id}
          setFilter={setFilter}
          rejectionOpen={rejectionCopy}
          setRejectionOpen={setRejectionCopy}
          allLocation={allLocation}
          modalType='Copy'
          editValue={selectedReferral}
          previousScrollPosition={previousScrollPosition}
        />
      )}

      {rejectionBulkDelete && (
        <BulkDeleteModal
          handleDelete={() => handleBulkDeleteRecord()}
          name={`${checkedIds?.length} records`}
          activeModal={rejectionBulkDelete}
          setActiveModal={setRejectionBulkDelete}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}
